import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, InputNumber, message, Space, Row, Col, Typography, Popconfirm, Tag, Upload, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, WalletOutlined, DownloadOutlined, UploadOutlined, PaperClipOutlined } from '@ant-design/icons';
import api from '../utils/api';
import CompanySelect from '../components/CompanySelect';
import { downloadCsv, yearOptions } from '../utils/exportCsv';
import { useDeepLinkWhen } from '../utils/deeplink';

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';

const defaultCategoryLabels = {
  staff_salary: 'Staff Salary',
  singer_payment: 'Singer Payment',
  production_cost: 'Production Cost',
  marketing: 'Marketing',
  office_expense: 'Office Expense',
  other_expense: 'Other Expense'
};

const categoryColors = {
  staff_salary: 'blue',
  singer_payment: 'purple',
  production_cost: 'orange',
  marketing: 'cyan',
  office_expense: 'gold',
  other_expense: 'default'
};

export default function Expenses() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  // AI answers open this page on the asked month (?month=YYYY-MM).
  const when = useDeepLinkWhen();
  const [filters, setFilters] = useState({
    company_id: '', category: '',
    month: when.month ? when.month.month() + 1 : new Date().getMonth() + 1,
    year: when.month ? when.month.year() : new Date().getFullYear(),
    page: 1
  });
  const [form] = Form.useForm();
  const [categoryLabels, setCategoryLabels] = useState({ ...defaultCategoryLabels });
  const [newCatName, setNewCatName] = useState('');
  const newCatInputRef = useRef(null);

  useEffect(() => { fetchCompanies(); fetchCategories(); }, []);
  useEffect(() => { fetchRecords();
    const poll = setInterval(() => fetchRecords(), 30000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/finance/expense-categories');
      const dbCats = res.data || [];
      const merged = { ...defaultCategoryLabels };
      dbCats.forEach(cat => {
        if (!merged[cat]) {
          merged[cat] = cat;
        }
      });
      setCategoryLabels(merged);
    } catch (err) { console.error(err); }
  };

  const addNewCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, '_');
    if (categoryLabels[key]) {
      message.warning('This category already exists');
      return;
    }
    setCategoryLabels(prev => ({ ...prev, [key]: name }));
    setNewCatName('');
    message.success(`"${name}" category added!`);
  };

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await api.get('/finance/expenses', { params: filters });
      setRecords(res.data.records);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async (values) => {
    try {
      // With a bill/receipt photo we send multipart, otherwise plain JSON.
      let payload = values;
      if (proofFile) {
        const fd = new FormData();
        Object.entries(values).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, v); });
        fd.append('proof', proofFile);
        payload = fd;
      }
      if (editing) {
        await api.put(`/finance/expenses/${editing.id}`, payload);
        message.success('Updated!');
      } else {
        await api.post('/finance/expenses', payload);
        message.success('Expense added!');
      }
      setModalVisible(false);
      setEditing(null);
      setProofFile(null);
      form.resetFields();
      fetchRecords();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/finance/expenses/${id}`); message.success('Deleted'); fetchRecords(); }
    catch (err) { message.error('Failed'); }
  };

  const totalAmount = records.reduce((s, r) => s + r.amount, 0);

  const columns = [
    { title: 'Company', dataIndex: 'company_name', key: 'company' },
    { title: 'Category', dataIndex: 'category', key: 'cat', render: (c) => <Tag color={categoryColors[c] || 'green'}>{categoryLabels[c] || c}</Tag> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => <Text strong style={{ color: '#ff4d4f' }}>₹{v.toLocaleString('en-IN')}</Text> },
    { title: 'Payee', dataIndex: 'payee_name', key: 'payee', render: (v) => v || '-' },
    { title: 'Bill/Proof', key: 'proof', render: (_, r) => r.proof_path ? <a href={`${API_BASE}/${r.proof_path}`} target="_blank" rel="noreferrer"><PaperClipOutlined /> View</a> : <Text type="secondary">-</Text> },
    { title: 'Description', dataIndex: 'description', key: 'desc', ellipsis: true },
    { title: 'Date', dataIndex: 'date', key: 'date', render: (v) => v || '-' },
    {
      title: 'Actions', key: 'actions', width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); setProofFile(null); form.setFieldsValue(r); setModalVisible(true); }} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><WalletOutlined style={{ color: '#ff4d4f' }} /> Expenses</Title>
          <Text type="secondary">Total: <Text strong style={{ color: '#ff4d4f' }}>₹{totalAmount.toLocaleString('en-IN')}</Text></Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/finance/expenses/export', { company_id: filters.company_id, category: filters.category, month: filters.month, year: filters.year })}>Download Excel</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setProofFile(null); form.resetFields(); form.setFieldsValue({ month: filters.month, year: filters.year }); setModalVisible(true); }}>Add Expense</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Select placeholder="Company" value={filters.company_id || undefined} onChange={(v) => setFilters({ ...filters, company_id: v || '' })} allowClear style={{ width: '100%' }}>
            {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <Select placeholder="Category" value={filters.category || undefined} onChange={(v) => setFilters({ ...filters, category: v || '' })} allowClear style={{ width: '100%' }}
            showSearch optionFilterProp="children"
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Space style={{ padding: '0 8px 4px' }}>
                  <Input
                    placeholder="New category"
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onPressEnter={addNewCategory}
                    style={{ width: 140 }}
                  />
                  <Button type="text" icon={<PlusOutlined />} onClick={addNewCategory}>Add</Button>
                </Space>
              </>
            )}
          >
            {Object.entries(categoryLabels).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <Select value={filters.month} onChange={(v) => setFilters({ ...filters, month: v })} style={{ width: '100%' }}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => <Option key={i+1} value={i+1}>{m}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <Select value={filters.year} onChange={(v) => setFilters({ ...filters, year: v })} style={{ width: '100%' }}>
            {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
        </Col>
      </Row>

      <Table dataSource={records} columns={columns} rowKey="id" loading={loading}
        pagination={{ current: filters.page, total, pageSize: 50, onChange: (p) => setFilters({ ...filters, page: p }) }} />

      <Modal title={editing ? 'Edit Expense' : 'Add Expense'} open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); }} onOk={() => form.submit()} width={600}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company_id" label="Company" rules={[{ required: true }]}>
                <CompanySelect companies={companies} onCompaniesChange={fetchCompanies} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Space style={{ padding: '0 8px 4px' }}>
                        <Input
                          placeholder="New category name"
                          ref={newCatInputRef}
                          value={newCatName}
                          onChange={(e) => setNewCatName(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          onPressEnter={addNewCategory}
                          style={{ width: 160 }}
                        />
                        <Button type="text" icon={<PlusOutlined />} onClick={addNewCategory}>
                          Add
                        </Button>
                      </Space>
                    </>
                  )}
                >
                  {Object.entries(categoryLabels).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payee_name" label="Payee Name"><Input /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="date" label="Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="month" label="Month"><InputNumber min={1} max={12} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="year" label="Year"><InputNumber min={2020} max={2030} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item label="Bill / Receipt Proof (photo/PDF)">
            <Upload
              beforeUpload={(f) => { setProofFile(f); return false; }}
              onRemove={() => setProofFile(null)}
              maxCount={1}
              fileList={proofFile ? [{ uid: '-1', name: proofFile.name }] : []}
              accept="image/*,application/pdf"
            >
              <Button icon={<UploadOutlined />}>Select bill/proof</Button>
            </Upload>
            {editing && editing.proof_path && !proofFile && (
              <div style={{ marginTop: 6 }}><a href={`${API_BASE}/${editing.proof_path}`} target="_blank" rel="noreferrer">View current proof</a></div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
